import { MoreOutlined } from '@ant-design/icons';
import * as antd from 'antd';
import { Content } from 'antd/lib/layout/layout';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import AcademicYearApiService from '../../../api/AcademicYearApiService';
import SecondaryProgramScenarioApiService from '../../../api/SecondaryProgramScenarioApiService';
import AuthorizedContent from '../../../components/AuthorizedContent';
import EditCancelButton from '../../../components/buttons/EditCancelButton';
import SecondaryProgramScenarioDetailsForm from '../../../components/forms/secondary/SecondaryProgramScenarioDetailsForm';
import Dropdown from '../../../components/inputs/Dropdown';
import Breadcrumbs from '../../../config/Breadcrumbs';
import Routes from '../../../config/Routes';
import FeatureFlag from '../../../consts/FeatureFlag';
import * as DeleteSecondaryProgramScenarioHandler from '../../../handlerModels/DeleteSecondaryProgramScenarioHandler';
import * as GetAcademicYearsHandler from '../../../handlerModels/GetAcademicYearsHandler';
import * as GetSecondaryProgramScenarioDetailsHandler from '../../../handlerModels/GetSecondaryProgramScenarioDetailsHandler';
import AcademicYearDTO from '../../../models/AcademicYearDTO';
import SecondaryProgramScenarioDTO from '../../../models/SecondaryProgramScenarioDTO';
import HeaderPortal from '../../../portals/HeaderPortal';
import BaseFormState from '../../../redux/bases/BaseFormState';
import Guid from '../../../utils/Guid';
import HistoryUtil from '../../../utils/HistoryUtil';
import ParameterUtil from '../../../utils/ParameterUtil';

interface SecondaryProgramScenarioDetailsPageState extends BaseFormState {
  id: string;
  SecondaryProgramScenarioId: number;
  SecondaryProgramScenario?: SecondaryProgramScenarioDTO;
  academicYears: AcademicYearDTO[];
  academicYearId: number;
  fromAcademicYearList: boolean;
  fromSelfStudies: boolean;
}

interface SecondaryProgramScenarioDetailsPageProps {
}

class SecondaryProgramScenarioDetailsPage extends React.Component<SecondaryProgramScenarioDetailsPageProps & RouteComponentProps<RouteObject>, SecondaryProgramScenarioDetailsPageState> {
  private readonly _secondaryProgramScenarioDetailsRef = React.createRef<SecondaryProgramScenarioDetailsForm>();

  constructor(props: SecondaryProgramScenarioDetailsPageProps & RouteComponentProps<RouteObject>) {
    super(props);

    this.state = {
      id: Guid.Empty(),
      SecondaryProgramScenarioId: 0,
      academicYears: [],
      academicYearId: 0,
      fromAcademicYearList: false,
      fromSelfStudies: false
    };
  }

  componentDidMount() {
    const SecondaryProgramScenarioId = ParameterUtil.getPathPart(this.props.match, 'id');
    const academicYearId = ParameterUtil.getParameter('academicYearId');
    const fromAcademicYearList = ParameterUtil.getParameter('fromAcademicYearList');
    const fromSelfStudies = ParameterUtil.getParameter('fromSelfStudies');
    this.setState({
      academicYearId: Number.parseInt(academicYearId),
      fromSelfStudies: fromSelfStudies == 'true',
      fromAcademicYearList: fromAcademicYearList == 'true'
    });

    if (this.props.match.path.split('/').findIndex(x => x == 'new') > -1) {
      this.setState({ isEditing: true, isNew: true, loading: false });
      const loaders = [];
      loaders.push(this.loadAcademicYears());
      Promise.all(loaders).then(() => {
        this.setState({ loading: false });
      });
    }
    else {
      const loaders = [];
      loaders.push(this.loadSecondaryProgramScenario(SecondaryProgramScenarioId));
      loaders.push(this.loadAcademicYears());

      Promise.all(loaders).then(() => {
        this.setState({ loading: false });
      });
    }
  }

  private loadSecondaryProgramScenario = (id: string, academicYear?: any, SecondaryProgramScenarioId?: any) => {
    this.setState({ id: id });
    const request = GetSecondaryProgramScenarioDetailsHandler.Request.create({
      id: id,
      academicYear: academicYear,
      SecondaryProgramScenarioId: SecondaryProgramScenarioId
    })

    SecondaryProgramScenarioApiService.getSecondaryProgramScenarioDetails(request)
      .then((results: GetSecondaryProgramScenarioDetailsHandler.Result) => {
        if (results.secondaryProgramScenario) {
          this.setState({
            SecondaryProgramScenario: results.secondaryProgramScenario,
            id: results.secondaryProgramScenario.id ?? Guid.Empty(),
            SecondaryProgramScenarioId: results.secondaryProgramScenario.secondaryProgramScenarioId,
            isNew: false,
            isEditing: false
          });
        }
        else {
          this.setState({
            isNew: true,
            id: Guid.Empty(),
            SecondaryProgramScenario: undefined,
            isEditing: true
          })
        }
      }).catch(() => {
        this.setState({ error: true });
      });
  }

  private loadAcademicYears = () => {
    this.setState({ loading: true });
    const request = GetAcademicYearsHandler.Request.create({
    });
    return AcademicYearApiService.getAcademicYears(request)
      .then((results: GetAcademicYearsHandler.Result) => {
        if (results && results.academicYears) {
          this.setState({
            academicYears: results.academicYears ?? [],
          });
        }
      })
      .catch(() => {
        this.setState({ error: true });
      });
  }

  private toggleEdit = () => {
    if (this.state.isEditing) {
      this._secondaryProgramScenarioDetailsRef.current?.resetForm();
    }

    this.setState({ isEditing: !this.state.isEditing });
  }

  private SecondaryProgramScenarioDetailsPageSaved = (SecondaryProgramScenarioId: string) => {
    this.setState({ isEditing: false, isNew: false });
    if (SecondaryProgramScenarioId != this.state.id) {
      HistoryUtil.push(Routes.generate(Routes.SECONDARY_PROGRAM_TYPE_DETAILS, { id: SecondaryProgramScenarioId }, { academicYearId: this.state.academicYearId, fromSelfStudies: this.state.fromSelfStudies, fromAcademicYearList: this.state.fromAcademicYearList }));
      this.loadSecondaryProgramScenario(SecondaryProgramScenarioId);
    }
    else {
      this.loadSecondaryProgramScenario(SecondaryProgramScenarioId);
    }
  }

  private deleteConfirmation = (id: string, academicYearId: number) => {
    antd.Modal.confirm({
      title: 'Are you sure you want to delete this ProgramType?',
      okText: 'Delete',
      onOk: () => this.deleteSecondaryProgramScenario(id, academicYearId)
    });
  }

  private deleteSecondaryProgramScenario = (SecondaryProgramScenarioId: string, academicYearId: number) => {
    this.setState({ submitting: true });

    const request = DeleteSecondaryProgramScenarioHandler.Request.create({
      SecondaryProgramScenarioId: SecondaryProgramScenarioId,
      academicYearId: academicYearId
    });

    SecondaryProgramScenarioApiService.deleteSecondaryProgramScenario(request)
      .then((result: DeleteSecondaryProgramScenarioHandler.Result | null) => {
        this.setState({
          error: !result?.succeeded,
          message: result?.errors.join('\n'),
          fieldErrors: result?.fieldErrors,
          altered: !result?.succeeded
        });

        if (result?.succeeded === true) {
          antd.message.success('ProgramType was removed.');
        }
        else {
          antd.message.error(result?.errors.join('\n'));
        }

      })
      .catch((results: any) => {
        this.setState({ error: results });
        antd.message.error('ProgramType could not be removed.');
      })
      .finally(() => {
        this.setState({ loading: false });
        this.goToListing();
      });
  }

  goToListing = () => {
    if (this.state.fromSelfStudies) {
      HistoryUtil.push(Routes.generate(Routes.SECONDARY_PROGRAM_TYPES, { academicYearId: this.state.academicYearId }, { fromSelfStudies: this.state.fromSelfStudies, fromAcademicYearList: this.state.fromAcademicYearList }));
    }
    else {
      HistoryUtil.push(Routes.generate(Routes.ALL_SECONDARY_PROGRAM_TYPES));
    }

  }

  private onSelect = (value: any) => {
    this.setState({ academicYearId: value });
    this.loadSecondaryProgramScenario(this.state.id, value, this.state.SecondaryProgramScenarioId);
  }

  render() {
    const title = this.state.loading ? '...' : this.state.isNew ? 'New ProgramType' : this.state.SecondaryProgramScenario?.title ?? '...';
    return (
      <Content >
        <HeaderPortal
          title={title}
          onBack={this.goToListing}
          breadcrumbs={Breadcrumbs.SecondaryProgramScenarioDetailPage(title, this.state.id, this.state.academicYearId, this.state.fromAcademicYearList, this.state.fromSelfStudies)}
          extra={this.renderMoreActions()}
        />

        {this.renderDetailsCard()}
      </Content >
    );
  }

  renderDetailsCard() {
    const editButton = this.state.id == Guid.Empty() ?
      null :
      (
        <AuthorizedContent validFeatureFlags={[FeatureFlag.EDIT_SECONDARY_PROGRAM_SCENARIO]}>
          <EditCancelButton isEditing={this.state.isEditing} onClick={this.toggleEdit} />
        </AuthorizedContent>
      );

    return (
      <antd.Card title="Details" extra={editButton} >
        <SecondaryProgramScenarioDetailsForm
          ref={this._secondaryProgramScenarioDetailsRef}
          SecondaryProgramScenarioId={this.state.id}
          isEditing={this.state.isEditing}
          isNew={this.state.isNew}
          onSave={this.SecondaryProgramScenarioDetailsPageSaved}
          academicYear={this.state.academicYearId} />
      </antd.Card>
    );
  }

  renderMoreActions() {
    const menu = (
      <antd.Menu>
        < antd.Menu.Item title='Delete ProgramType' key={this.state.id} onClick={() => this.deleteConfirmation(this.state.id, this.state.academicYearId)}  >
          Delete ProgramType
        </antd.Menu.Item>
      </antd.Menu>
    );
    return (
      <>
        <Dropdown value={this.state.academicYearId} onSelect={this.onSelect} dropdownMatchSelectWidth={false}>{this.state.academicYears.map(x => { return this.renderAcademicYear(x); })}</Dropdown>

        <AuthorizedContent validFeatureFlags={[FeatureFlag.EDIT_SECONDARY_PROGRAM_SCENARIO]}>
          {this.state.isNew ? null
            : <antd.Dropdown overlay={menu}>
              <antd.Button size='large' type='link' >
                <MoreOutlined />
              </antd.Button>
            </antd.Dropdown>}
        </AuthorizedContent>
      </>
    );
  }
  renderAcademicYear(academicYear: AcademicYearDTO) {
    return <antd.Select.Option key={academicYear.id} value={academicYear.id}>{academicYear.id}</antd.Select.Option>
  }
}

export default withRouter(SecondaryProgramScenarioDetailsPage);
